import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { TableCell, TableRow, Checkbox } from '@mui/material';
import { TextField, IconButton, Autocomplete } from '@totalsoft/rocket-ui';
import CancelIcon from '@mui/icons-material/Cancel';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import PersonIcon from '@mui/icons-material/Person';
import debounce from 'lodash.debounce';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import OfferPartnerShareholderParentRowComponent from './OfferPartnerShareholderParentRowComponent';
import { withTranslation } from 'react-i18next';
import { ParamsContext } from 'providers/ParamsContext';
import PhoneNumberField from 'components/common/inputs/PhoneNumberTextField';
import { BoxActionsWrapper } from 'components/common/CommonStyles';

export const OfferPartnerShareholderRowComponent = ({
  disabled,
  shareholder,
  loadPartners,
  onPropertyChange,
  onClearShareholder,
  deleteShareholder,
  loadPartnerDetails,
  idx,
  validationShareholderSelection,
  openMasterPartner,
  openDocumentPartner,
  masterPartnerApp,
  t,
  isAssociatedPartner,
  partnerTypes,
  viewShareholderPhone
}) => {
  const onShareholderChange = onPropertyChange => suggestion => {
    if (suggestion) return onPropertyChange(suggestion.id ? suggestion.id : -1, suggestion.uniqueID);
    return onClearShareholder(suggestion);
  };
  const { cvsChPercent } = useContext(ParamsContext);

  return (
    <>
      <TableRow>
        <TableCell width='20%'>
          <div id={'shareholder_' + idx + '_uniqueID'}>
            <Autocomplete
              fullWidth
              value={shareholder || ''}
              creatable
              isClearable
              onChange={onShareholderChange(onPropertyChange('shareholderPartnerId', 'uniqueId', loadPartnerDetails))}
              valueKey='id'
              labelKey='uniqueID'
              error={!isValid(validationShareholderSelection[idx].uniqueID)}
              helperText={getErrors(validationShareholderSelection[idx].uniqueID)}
              loadOptions={debounce(loadPartners, 500, { leading: true })}
              disabled={disabled}
            />
          </div>
        </TableCell>
        <TableCell width='20%'>
          <div id={'shareholder_' + idx + '_partnerType'}>
            <Autocomplete
              fullWidth
              value={shareholder.partnerTypeId}
              labelKey='partnerTypeName'
              valueKey='partnerTypeId'
              simpleValue
              options={partnerTypes}
              onChange={onPropertyChange('partnerTypeId')}
              error={!isValid(validationShareholderSelection[idx].partnerTypeId)}
              helperText={getErrors(validationShareholderSelection[idx].partnerTypeId)}
              disabled={shareholder.partnerExists}
            />
          </div>
        </TableCell>
        <TableCell width='20%'>
          <TextField
            id={'shareholder_' + idx + '_partnerName'}
            name={'shareHolderPartnerName'}
            fullWidth
            value={shareholder.shareHolderPartnerName || ''}
            onChange={onPropertyChange('shareHolderPartnerName')}
            error={!isValid(validationShareholderSelection[idx].shareHolderPartnerName)}
            helperText={getErrors(validationShareholderSelection[idx].shareHolderPartnerName)}
            disabled={disabled}
          />
        </TableCell>
        {viewShareholderPhone && (
          <TableCell width='20%'>
            <PhoneNumberField
              id={'shareholder_' + idx + '_phoneNumber'}
              name={'shareHolderPhoneNumber'}
              fullWidth
              value={shareholder.shareHolderPhoneNumber || ''}
              onChange={onPropertyChange('shareHolderPhoneNumber')}
              error={!isValid(validationShareholderSelection[idx].shareHolderPhoneNumber)}
              helperText={getErrors(validationShareholderSelection[idx].shareHolderPhoneNumber)}
              disabled={disabled}
            />
          </TableCell>
        )}
        <TableCell width='15%'>
          <TextField
            id={'shareholder_' + idx + '_sharePercent'}
            name={'sharePercent'}
            fullWidth
            isNumeric
            inputProps={{ decimalScale: cvsChPercent }}
            value={shareholder.sharePercent || ''}
            onChange={onPropertyChange('sharePercent')}
            error={!isValid(validationShareholderSelection[idx].sharePercent)}
            helperText={getErrors(validationShareholderSelection[idx].sharePercent)}
            disabled={disabled}
          />
        </TableCell>
        <TableCell align='center'>
          <Checkbox
            id={'shareholder_' + idx + '_isRealBeneficiary'}
            name={'isRealBeneficiary'}
            checked={shareholder.isRealBeneficiary}
            onChange={onCheckBoxChange(onPropertyChange('isRealBeneficiary'))}
            disabled={disabled}
          />
        </TableCell>
        <TableCell>
          <BoxActionsWrapper>
            {masterPartnerApp ? (
              <IconButton
                id={'shareholder_' + idx + '_editMasterPartner'}
                color='primary'
                size='small'
                variant='text'
                tooltip={t('Tooltips.EditPartner')}
                disabled={shareholder.shareholderPartnerId === null && shareholder.documentPartnerId === null}
                onClick={
                  !shareholder.documentPartnerId
                    ? openMasterPartner(shareholder.shareholderPartnerId)
                    : openDocumentPartner(shareholder.documentPartnerId, shareholder.shareHolderPartnerName, 'Shareholder')
                }
              >
                <PersonIcon />
              </IconButton>
            ) : null}
            <IconButton
              id={'shareholder_' + idx + '_deleteBtn'}
              color='primary'
              size='small'
              variant='text'
              onClick={deleteShareholder}
              disabled={disabled}
            >
              <CancelIcon />
            </IconButton>
          </BoxActionsWrapper>
        </TableCell>
      </TableRow>
      {shareholder.shareholders &&
        shareholder.shareholders.map((parentShareholder, idx) => {
          return (
            <OfferPartnerShareholderParentRowComponent
              key={idx}
              idx={idx}
              shareholder={parentShareholder}
              openMasterPartner={openMasterPartner}
              openDocumentPartner={openDocumentPartner}
              masterPartnerApp={masterPartnerApp}
              isAssociatedPartner={isAssociatedPartner}
              viewShareholderPhone={viewShareholderPhone}
            />
          );
        })}
    </>
  );
};

OfferPartnerShareholderRowComponent.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onClearShareholder: PropTypes.func.isRequired,
  shareholder: PropTypes.object.isRequired,
  loadPartners: PropTypes.func.isRequired,
  deleteShareholder: PropTypes.func.isRequired,
  loadPartnerDetails: PropTypes.func.isRequired,
  idx: PropTypes.number,
  validationShareholderSelection: PropTypes.object,
  openMasterPartner: PropTypes.func.isRequired,
  openDocumentPartner: PropTypes.func.isRequired,
  masterPartnerApp: PropTypes.bool,
  t: PropTypes.func.isRequired,
  params: PropTypes.object,
  isAssociatedPartner: PropTypes.bool.isRequired,
  partnerTypes: PropTypes.array.isRequired,
  viewShareholderPhone: PropTypes.bool.isRequired
};

export default withTranslation()(OfferPartnerShareholderRowComponent);
